import Intersections from '../item/lineTree/intersections';
import InPolyCollision from './collision/inPolyCollision';
import IntersectionCollision from './collision/intersectionCollision';

/**
 * Check for collisions between collision trees
 */
export default class TreeCollisionChecker {
    /**
     * @type {PolygonCollisionChecker}
     */
    polygonCollisionChecker = null;

    /**
     * @param {PolygonCollisionChecker} polygonCollisionChecker
     */
    constructor(polygonCollisionChecker) {
        this.polygonCollisionChecker = polygonCollisionChecker;
    }

    /**
     * Check the given item against all its siblings
     *
     * @param {Tree} leadingTree
     * @param {Tree[]} otherTrees
     * @return {AbstractCollision[]} Array with found collisions
     */
    check(leadingTree, otherTrees) {
        const collisions = [];

        // eslint-disable-next-line no-restricted-syntax
        for (const otherTree of otherTrees) {
            const collision = this.checkTree(leadingTree, otherTree);

            if (collision) collisions.push(collision);
        }

        return collisions;
    }

    /**
     * @param {Tree} leadingTree
     * @param {Tree} otherTree
     *
     * @return {AbstractCollision|null}
     */
    checkTree(leadingTree, otherTree) {
        // pre-check bound collisions
        if (!this._hasBoundsCollisions(leadingTree, otherTree)) return null;

        if (leadingTree.bounds.equal(otherTree.bounds)) {
            return new InPolyCollision(leadingTree.item, otherTree.item);
        }

        // detail check for intersections
        const intersections = leadingTree.intersect(otherTree);
        if (intersections.count()) {
            // TODO: Dont access item??
            return new IntersectionCollision(leadingTree.item, otherTree.item, intersections);
        }

        const leadingLines = leadingTree.lines;
        const otherLines = otherTree.lines;
        const leadingBounds = leadingTree.bounds;
        const otherBounds = otherTree.bounds;

        // check if leading item inside of otherItem
        if (otherBounds.contains(leadingBounds)
            && this.polygonCollisionChecker.isPointInPoly(leadingLines[0].startPoint, otherLines)) {
            // TODO: Dont access item??
            return new InPolyCollision(leadingTree.item, otherTree.item);
        }

        // check if otherItem inside of leading item
        if (leadingBounds.contains(otherBounds)
            && this.polygonCollisionChecker.isPointInPoly(otherLines[0].startPoint, leadingLines)) {
            // TODO: Dont access item??
            return new InPolyCollision(otherTree.item, leadingTree.item);
        }

        // Check if the center of any line is inside the other item
        // Edge case OFCS-4776
        // eslint-disable-next-line no-restricted-syntax
        for (const line of leadingLines) {
            if (this.polygonCollisionChecker.isPointInPoly(line.getCenter(), otherLines)) {
                const centerIntersections = new Intersections();
                centerIntersections.add(line.getCenter());
                return new IntersectionCollision(otherTree.item, leadingTree.item, centerIntersections);
            }
        }

        return null;
    }

    /**
     * @param {Tree} leadingTree
     * @param {Tree} otherTree
     *
     * @return {boolean}
     * @private
     */
    _hasBoundsCollisions(leadingTree, otherTree) {
        return leadingTree.bounds.intersect(otherTree.bounds)
            || otherTree.bounds.contains(leadingTree.bounds)
            || leadingTree.bounds.contains(otherTree.bounds);
    }
}
